import { useAtom } from 'jotai';
import { userAtom, accessTokenAtom } from '../../../atoms';
import { Navigate } from 'react-router-dom';
import { paths } from '../../../../config/paths';
import { useCallback, useEffect, useState } from 'react';

interface WithdrawRequest {
    user: {
        id: string;
        email: string;
        fujiyama_telegram_id: string | null;
        fujiyama_referral_code: string | null;
    };
    activity: {
        id: string;
        status: string;
        wallet_chain: string | null;
        wallet_address: string | null;
        wallet_transaction_id: string | null;
        amount: number;
    };
}

interface WithdrawRequestsResponse {
    requests: WithdrawRequest[];
    total: number;
    page: number;
    hasMore: boolean;
}

/* const useMigration = () => {
    const [user] = useAtom(userAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const [isMigrating, setIsMigrating] = useState(false);

    const executeMigration = useCallback(async () => {
        if (!user || !accessToken) return;

        setIsMigrating(true);
        /*
        try {
            const response = await fetch('/api/v1/admin/migrate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('マイグレーションの実行に失敗しました');
            }

            const result = await response.json();
            alert('マイグレーションが完了しました');
            console.log('Migration result:', result);
        } catch (err) {
            alert(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
        } finally {
            setIsMigrating(false);
        }
            *//*
}, [user, accessToken]);

return {
executeMigration,
isMigrating
};
}; */

const useWithdrawRequests = (page: number) => {
    const [user] = useAtom(userAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<WithdrawRequestsResponse | null>(null);
    const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

    const fetchRequests = useCallback(async () => {
        if (!user || !accessToken) {
            setError('認証情報が見つかりません');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`/api/v1/admin/withdraw-request?page=${page}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('リクエストの取得に失敗しました');
            }

            const responseData: WithdrawRequestsResponse = await response.json();
            setData(responseData);
        } catch (err) {
            setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
        } finally {
            setIsLoading(false);
        }
    }, [user, accessToken, page]);

    const approveRequest = useCallback(async (activityId: string, transactionId: string) => {
        if (!user || !accessToken) return;

        setProcessingIds(prev => new Set(prev).add(activityId));
        try {
            const response = await fetch('/api/v1/admin/withdraw-request', {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    requests: [{
                        id: activityId,
                        status: 'processed',
                        wallet_transaction_id: transactionId
                    }]
                })
            });

            if (!response.ok) {
                throw new Error('承認処理に失敗しました');
            }

            // ローカルでデータを更新
            setData(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    requests: prev.requests.map(request =>
                        request.activity.id === activityId
                            ? {
                                ...request,
                                activity: {
                                    ...request.activity,
                                    status: 'processed',
                                    wallet_transaction_id: transactionId
                                }
                            }
                            : request
                    )
                };
            });
        } catch (err) {
            setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
        } finally {
            setProcessingIds(prev => {
                const next = new Set(prev);
                next.delete(activityId);
                return next;
            });
        }
    }, [user, accessToken]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    return {
        data,
        isLoading,
        error,
        refetch: fetchRequests,
        approveRequest,
        processingIds
    };
};

export const WithdrawRequestsPage = () => {
    const [user] = useAtom(userAtom);
    const [currentPage, setCurrentPage] = useState(1);
    const [transactionIds, setTransactionIds] = useState<{ [key: string]: string }>({});
    const {
        data,
        isLoading,
        error,
        approveRequest,
        processingIds
    } = useWithdrawRequests(currentPage);

    if (!user?.has_admin_role) {
        return <Navigate to={paths.dashboard.path} replace />;
    }

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const handleNextPage = () => {
        if (data?.hasMore) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handleTransactionIdChange = (activityId: string, value: string) => {
        setTransactionIds(prev => ({
            ...prev,
            [activityId]: value
        }));
    };

    const handleApprove = async (activityId: string) => {
        const transactionId = transactionIds[activityId];
        if (transactionId) {
            await approveRequest(activityId, transactionId);
            setTransactionIds(prev => {
                const next = { ...prev };
                delete next[activityId];
                return next;
            });
        }
    };

    return (
        <div className="max-w-6xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">出金リクエスト一覧</h1>
                {/* <button
                    onClick={executeMigration}
                    disabled={isMigrating}
                    className={`px-4 py-2 rounded-md ${isMigrating
                        ? 'bg-red-300 cursor-not-allowed'
                        : 'bg-red-500 hover:bg-red-600'
                        } text-white font-medium`}
                >
                    {isMigrating ? 'マイグレーション実行中...' : 'マイグレーション実行'}
                </button> */}
            </div>
            <div className="bg-white shadow rounded-lg p-6">
                <div className="space-y-4">
                    {error && (
                        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
                            {error}
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        ユーザー情報
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        金額
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        ステータス
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        ウォレット情報
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        アクション
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-4 text-center">
                                            <div className="flex justify-center items-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : data?.requests.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                                            リクエストが見つかりませんでした
                                        </td>
                                    </tr>
                                ) : (
                                    data?.requests.map((request) => (
                                        <tr key={request.activity.id}>
                                            <td className="px-6 py-4">
                                                <div className="text-sm">
                                                    <div className="font-medium text-gray-900">{request.user.email}</div>
                                                    {request.user.fujiyama_telegram_id && (
                                                        <div className="text-gray-500">
                                                            Telegram ID: {request.user.fujiyama_telegram_id}
                                                        </div>
                                                    )}
                                                    {request.user.fujiyama_referral_code && (
                                                        <div className="text-gray-500">
                                                            Fujiyama Referral Code: {request.user.fujiyama_referral_code}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-medium text-gray-900">
                                                    ${request.activity.amount.toLocaleString()}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${request.activity.status === 'waiting'
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : request.activity.status === 'processed'
                                                        ? 'bg-green-100 text-green-800'
                                                        : request.activity.status === 'checking'
                                                            ? 'bg-blue-100 text-blue-800'
                                                            : 'bg-gray-100 text-gray-800'
                                                    }`}>
                                                    {request.activity.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900">
                                                    {request.activity.wallet_chain && (
                                                        <div>Chain: {request.activity.wallet_chain}</div>
                                                    )}
                                                    {request.activity.wallet_address && (
                                                        <div className="truncate max-w-xs">
                                                            送金先: {request.activity.wallet_address}
                                                        </div>
                                                    )}
                                                    {request.activity.wallet_transaction_id && (
                                                        <div className="truncate max-w-xs">
                                                            TXID: {request.activity.wallet_transaction_id}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm">
                                                {request.activity.status === 'checking' && (
                                                    <div className="flex flex-col space-y-2">
                                                        <input
                                                            type="text"
                                                            value={transactionIds[request.activity.id] || ''}
                                                            onChange={(e) => handleTransactionIdChange(request.activity.id, e.target.value)}
                                                            placeholder="TXID"
                                                            className="px-2 py-1 border rounded-md text-sm"
                                                        />
                                                        <button
                                                            onClick={() => handleApprove(request.activity.id)}
                                                            disabled={!transactionIds[request.activity.id] || processingIds.has(request.activity.id)}
                                                            className={`px-4 py-2 rounded-md ${!transactionIds[request.activity.id] || processingIds.has(request.activity.id)
                                                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                                : 'bg-blue-500 text-white hover:bg-blue-600'
                                                                }`}
                                                        >
                                                            確定
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ページネーション */}
                    {data && (
                        <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center">
                                <span className="text-sm text-gray-700">
                                    全{data.total}件中 {(currentPage - 1) * 50 + 1}-
                                    {Math.min(currentPage * 50, data.total)}件を表示
                                </span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={handlePreviousPage}
                                    disabled={currentPage === 1}
                                    className={`px-3 py-1 border rounded-md ${currentPage === 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    前へ
                                </button>
                                <button
                                    onClick={handleNextPage}
                                    disabled={!data.hasMore}
                                    className={`px-3 py-1 border rounded-md ${!data.hasMore
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                >
                                    次へ
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WithdrawRequestsPage;